// Establecimientos → puntos de emisión → secuencias por tipo de documento.
// Jerarquía SRI real embebida en Configuración de Emisión. Cada punto tiene su
// código, nombre, AMBIENTE propio y un último secuencial por tipo (01/04/06/07).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, MapPin, Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/api/client';
import { Modal, Campo, Switch, fieldCls } from '@/features/inventario/shared';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
const TIPOS = [{ cod: '01', label: 'Factura' }, { cod: '04', label: 'N. Crédito' }, { cod: '06', label: 'Guía rem.' }, { cod: '07', label: 'Retención' }];

export interface Establecimiento { id: string; codigo: string; nombre: string | null; dir_establecimiento: string | null; ciudad: string | null; es_matriz: boolean; activo: boolean }
export interface Punto { id: string; establecimiento_id: string; est_codigo: string; est_nombre: string | null; codigo: string; nombre: string | null; ambiente: string; activo: boolean; secuencias: Record<string, number> }

export async function estListar() { return (await apiClient.post('/rpc/est_listar', {}, FIN)).data as Establecimiento[]; }
export async function ptoListar() { return (await apiClient.post('/rpc/pto_listar', {}, FIN)).data as Punto[]; }

export function EstablecimientosPuntos() {
  const qc = useQueryClient();
  const { data: ests = [] } = useQuery<Establecimiento[]>({ queryKey: ['establecimientos'], queryFn: estListar });
  const { data: ptos = [] } = useQuery<Punto[]>({ queryKey: ['puntos-emision'], queryFn: ptoListar });
  const refetch = () => { void qc.invalidateQueries({ queryKey: ['establecimientos'] }); void qc.invalidateQueries({ queryKey: ['puntos-emision'] }); };

  const [estForm, setEstForm] = useState<any | null>(null);
  const [ptoForm, setPtoForm] = useState<any | null>(null);
  const err = (e: any) => toast.error(e?.response?.data?.message || 'No se pudo completar');

  const guardarEst = useMutation({
    mutationFn: async () => (await apiClient.post('/rpc/est_guardar', { _id: estForm.id, _codigo: estForm.codigo, _nombre: estForm.nombre || null, _dir: estForm.dir || null, _activo: estForm.activo, _ciudad: estForm.ciudad || null }, FIN)).data,
    onSuccess: () => { toast.success('Establecimiento guardado'); setEstForm(null); refetch(); }, onError: err,
  });
  const eliminarEst = useMutation({ mutationFn: (id: string) => apiClient.post('/rpc/est_eliminar', { _id: id }, FIN), onSuccess: () => { toast.success('Establecimiento eliminado'); refetch(); }, onError: err });
  const guardarPto = useMutation({
    mutationFn: async () => (await apiClient.post('/rpc/pto_guardar', {
      _id: ptoForm.id, _establecimiento_id: ptoForm.establecimiento_id, _codigo: ptoForm.codigo,
      _nombre: ptoForm.nombre || null, _ambiente: ptoForm.ambiente, _activo: ptoForm.activo, _secuencias: ptoForm.secuencias,
    }, FIN)).data,
    onSuccess: () => { toast.success('Punto de emisión guardado'); setPtoForm(null); refetch(); }, onError: err,
  });
  const eliminarPto = useMutation({ mutationFn: (id: string) => apiClient.post('/rpc/pto_eliminar', { _id: id }, FIN), onSuccess: () => { toast.success('Punto eliminado'); refetch(); }, onError: err });

  const nuevoEst = () => setEstForm({ id: null, codigo: '', nombre: '', dir: '', ciudad: '', activo: true, es_matriz: false });
  const editarEst = (e: Establecimiento) => setEstForm({ id: e.id, codigo: e.codigo, nombre: e.nombre || '', dir: e.dir_establecimiento || '', ciudad: e.ciudad || '', activo: e.activo, es_matriz: e.es_matriz });
  const nuevoPto = (estId: string) => setPtoForm({ id: null, establecimiento_id: estId, codigo: '', nombre: '', ambiente: '1', activo: true, secuencias: { '01': '0', '04': '0', '06': '0', '07': '0' } });
  const editarPto = (p: Punto) => setPtoForm({ id: p.id, establecimiento_id: p.establecimiento_id, codigo: p.codigo, nombre: p.nombre || '', ambiente: p.ambiente, activo: p.activo, secuencias: Object.fromEntries(TIPOS.map((t) => [t.cod, String(p.secuencias?.[t.cod] ?? 0)])) });

  const dlg = useDialogoEnLinea();

  return (
    <Card>
      {dlg.ui}
      <CardHeader className="pb-2"><div className="flex items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2"><Building2 className="w-4 h-4" /> Establecimientos y puntos de emisión</CardTitle>
        <Button size="sm" variant="outline" onClick={nuevoEst}><Plus className="w-4 h-4 mr-1" /> Establecimiento</Button>
      </div></CardHeader>
      <CardContent className="space-y-4">
        {ests.length === 0 && <p className="text-sm text-muted-foreground">Aún no hay establecimientos. Crea el primero.</p>}
        {ests.map((e) => {
          const suyos = ptos.filter((p) => p.establecimiento_id === e.id);
          return (
            <div key={e.id} className="rounded-lg border">
              <div className="flex items-center justify-between gap-2 border-b bg-muted/30 px-3 py-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <span className="font-mono">{e.codigo}</span> {e.nombre || 'Establecimiento'}
                    {e.es_matriz && <span className="rounded bg-primary/10 px-1 text-[10px] text-primary">matriz</span>}
                    {!e.activo && <span className="text-[10px] text-muted-foreground">(inactivo)</span>}
                  </div>
                  <p className="truncate text-[11px] text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" /> {e.dir_establecimiento || 'sin dirección'}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => nuevoPto(e.id)}><Plus className="w-4 h-4 mr-1" /> Punto</Button>
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => editarEst(e)}><Pencil className="w-4 h-4" /></button>
                  {!e.es_matriz && <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10" title="Eliminar" onClick={async () => { if (await dlg.confirmar(`¿Eliminar establecimiento ${e.codigo}?`, { peligro: true })) eliminarEst.mutate(e.id); }}><Trash2 className="w-4 h-4" /></button>}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="py-1 px-3">Punto</th><th className="py-1 px-2">Nombre</th><th className="py-1 px-2">Ambiente</th>
                    {TIPOS.map((t) => <th key={t.cod} className="py-1 px-2 text-right">{t.label}</th>)}
                    <th className="py-1 px-2"></th>
                  </tr></thead>
                  <tbody>
                    {suyos.length === 0 && <tr><td colSpan={4 + TIPOS.length} className="py-2 px-3 text-xs text-muted-foreground">Sin puntos de emisión — agrega uno.</td></tr>}
                    {suyos.map((p) => (
                      <tr key={p.id} className="border-b last:border-0">
                        <td className="py-1 px-3 font-mono">{e.codigo}-{p.codigo}{!p.activo && <span className="ml-1 text-[10px] text-muted-foreground">(inact.)</span>}</td>
                        <td className="py-1 px-2">{p.nombre || '—'}</td>
                        <td className="py-1 px-2">{p.ambiente === '2'
                          ? <span className="rounded bg-green-100 px-1 text-[10px] text-green-700 dark:bg-green-900/40 dark:text-green-300">Producción</span>
                          : <span className="rounded bg-amber-100 px-1 text-[10px] text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">Pruebas</span>}</td>
                        {TIPOS.map((t) => <td key={t.cod} className="py-1 px-2 text-right font-mono tabular-nums">{p.secuencias?.[t.cod] ?? 0}</td>)}
                        <td className="py-1 px-2 text-right whitespace-nowrap">
                          <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => editarPto(p)}><Pencil className="w-4 h-4" /></button>
                          <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 ml-1" title="Eliminar" onClick={async () => { if (await dlg.confirmar(`¿Eliminar punto ${e.codigo}-${p.codigo}?`, { peligro: true })) eliminarPto.mutate(p.id); }}><Trash2 className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
        <p className="text-[11px] text-muted-foreground">El número por tipo es el <strong>último secuencial emitido</strong> en esa serie (estab-punto); la próxima emisión usará ese número + 1. Cada punto tiene su propio <strong>ambiente</strong>.</p>
      </CardContent>

      {estForm && (
        <Modal title={estForm.id ? 'Editar establecimiento' : 'Nuevo establecimiento'} onClose={() => setEstForm(null)}>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Código (3 díg.)"><input className={fieldCls} maxLength={3} placeholder="001" value={estForm.codigo} disabled={estForm.es_matriz} onChange={(e) => setEstForm({ ...estForm, codigo: e.target.value })} /></Campo>
              <Campo label="Nombre" className="col-span-2"><input className={fieldCls} placeholder="Sucursal Norte" value={estForm.nombre} onChange={(e) => setEstForm({ ...estForm, nombre: e.target.value })} /></Campo>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Dirección del establecimiento" className="col-span-2"><input className={fieldCls} value={estForm.dir} onChange={(e) => setEstForm({ ...estForm, dir: e.target.value })} /></Campo>
              <Campo label="Ciudad"><input className={fieldCls} placeholder="GUAYAQUIL" value={estForm.ciudad} onChange={(e) => setEstForm({ ...estForm, ciudad: e.target.value })} /></Campo>
            </div>
            <Switch checked={estForm.activo} onChange={(v) => setEstForm({ ...estForm, activo: v })} label="Activo" />
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setEstForm(null)}>Cancelar</Button>
              <Button isLoading={guardarEst.isPending} disabled={!/^\d{3}$/.test(estForm.codigo)} onClick={() => guardarEst.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}

      {ptoForm && (
        <Modal title={ptoForm.id ? 'Editar punto de emisión' : 'Nuevo punto de emisión'} onClose={() => setPtoForm(null)}>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Punto (3 díg.)"><input className={fieldCls} maxLength={3} placeholder="001" value={ptoForm.codigo} onChange={(e) => setPtoForm({ ...ptoForm, codigo: e.target.value })} /></Campo>
              <Campo label="Nombre" className="col-span-2"><input className={fieldCls} placeholder="Caja 1 / Oficina" value={ptoForm.nombre} onChange={(e) => setPtoForm({ ...ptoForm, nombre: e.target.value })} /></Campo>
            </div>
            <Campo label="Ambiente">
              <select className={fieldCls} value={ptoForm.ambiente} onChange={(e) => setPtoForm({ ...ptoForm, ambiente: e.target.value })}>
                <option value="1">Pruebas / certificación</option>
                <option value="2">Producción</option>
              </select>
            </Campo>
            <div>
              <p className="text-xs font-medium mb-1">Último secuencial por tipo de documento</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {TIPOS.map((t) => (
                  <Campo key={t.cod} label={t.label}>
                    <input className={fieldCls} type="number" min={0} value={ptoForm.secuencias[t.cod]} onChange={(e) => setPtoForm({ ...ptoForm, secuencias: { ...ptoForm.secuencias, [t.cod]: e.target.value } })} />
                  </Campo>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">La próxima emisión de cada tipo usará el número + 1.</p>
            </div>
            <Switch checked={ptoForm.activo} onChange={(v) => setPtoForm({ ...ptoForm, activo: v })} label="Activo" />
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setPtoForm(null)}>Cancelar</Button>
              <Button isLoading={guardarPto.isPending} disabled={!/^\d{3}$/.test(ptoForm.codigo)} onClick={() => guardarPto.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </Card>
  );
}
